import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, RefreshCw } from "lucide-react";
import { formatCPF, formatPhone } from "@/lib/formatters";
import { cleanCPF, generateValidCPF } from "@/lib/cpf-validator";

interface PacienteDadosBasicosProps {
  patientId: string;
}

interface PatientFormData {
  full_name: string;
  cpf: string | null;
  email: string | null;
  celular: string | null;
  data_nascimento: string | null;
  sexo: string | null;
}

const PacienteDadosBasicos = ({ patientId }: PacienteDadosBasicosProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PatientFormData>({
    full_name: "",
    cpf: null,
    email: null,
    celular: null,
    data_nascimento: null,
    sexo: null,
  });

  useEffect(() => {
    loadPatientData();
  }, [patientId]);

  const loadPatientData = async () => {
    try {
      console.log('🔍 Carregando dados básicos do paciente:', patientId);

      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", patientId)
        .single();

      if (error) {
        console.error('❌ Erro ao carregar dados:', error);
        throw error;
      }

      console.log('✅ Dados básicos carregados:', data);
      if (data) {
        setFormData({
          full_name: data.full_name || "",
          cpf: data.cpf || null,
          email: data.email || null,
          celular: data.celular || null,
          data_nascimento: data.data_nascimento || null,
          sexo: data.sexo || null,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados do paciente");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validação básica
    if (!formData.full_name?.trim()) {
      toast.error("Nome completo é obrigatório");
      return;
    }

    setSaving(true);
    try {
      console.log('💾 Salvando dados do paciente:', patientId);

      const { error } = await supabase
        .from("patients_onboarding")
        .update({
          full_name: formData.full_name.trim(),
          cpf: formData.cpf ? cleanCPF(formData.cpf) : null,
          email: formData.email?.trim() || null,
          celular: formData.celular ? formData.celular.replace(/\D/g, '') : null,
          data_nascimento: formData.data_nascimento || null,
          sexo: formData.sexo || null,
        })
        .eq("id", patientId);

      if (error) {
        console.error('❌ Erro ao salvar:', error);
        throw error;
      }

      console.log('✅ Dados salvos com sucesso');
      toast.success("Dados atualizados com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao salvar:", error);
      toast.error("Erro ao salvar dados");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados Básicos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 col-span-2">
            <Label htmlFor="full_name">Nome Completo</Label>
            <Input
              id="full_name"
              value={formData.full_name || ""}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cpf">CPF</Label>
            <div className="flex gap-2">
              <Input
                id="cpf"
                value={formData.cpf ? formatCPF(formData.cpf) : ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setFormData({ ...formData, cpf: value });
                }}
                maxLength={14}
                placeholder="000.000.000-00"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setFormData({ ...formData, cpf: generateValidCPF() })}
                title="Gerar CPF válido"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="data_nascimento">Data de Nascimento</Label>
            <Input
              id="data_nascimento"
              type="date"
              value={formData.data_nascimento || ""}
              onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ""}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="celular">Celular</Label>
            <Input
              id="celular"
              value={formData.celular ? formatPhone(formData.celular) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setFormData({ ...formData, celular: value });
              }}
              maxLength={15}
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sexo">Sexo</Label>
            <Select
              value={formData.sexo || ""}
              onValueChange={(value) => setFormData({ ...formData, sexo: value })}
            >
              <SelectTrigger id="sexo">
                <SelectValue placeholder="Selecione o sexo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Masculino">Masculino</SelectItem>
                <SelectItem value="Feminino">Feminino</SelectItem>
                <SelectItem value="Outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export { PacienteDadosBasicos };
